import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import {
  CAP_TABLE_VERSIONS_BY_COMPANY_AND_MD_DEFAULT_ERROR_MESSAGE,
  CAP_TABLE_VERSIONS_BY_COMPANY_AND_MD_QUERY_KEY,
} from 'common/constants/services/companies';
import { CompaniesService } from 'services';
import { throwError } from 'utilities';
import {
  GetCapTableVersionsByCompanyAndMDHook,
  GetCapTableVersionsByCompanyAndMDParams,
  GetCapTableVersionsByCompanyAndMDResponse,
  UseGetCapTableVersionsByCompanyAndMD,
} from './types';

export const getCapTableVersionsByCompanyAndMD = async (params: GetCapTableVersionsByCompanyAndMDParams) => {
  const { companyId, measurementDateId, companyMeasurementDateId, excludePrivateVersions } = params;

  if (isNil(companyId) && (isNil(measurementDateId) || isNil(companyMeasurementDateId))) {
    return null;
  }

  const companiesService = new CompaniesService();

  try {
    const capTableVersions = (await companiesService.getCapTableList(`${companyId}`, {
      companyMeasurementDateId:
        companyMeasurementDateId != null ? `${companyMeasurementDateId}` : undefined,
      excludePrivateVersions,
      measurementDateId: measurementDateId != null ? `${measurementDateId}` : undefined,
    })) as GetCapTableVersionsByCompanyAndMDResponse;

    if (!capTableVersions) {
      return throwError(CAP_TABLE_VERSIONS_BY_COMPANY_AND_MD_DEFAULT_ERROR_MESSAGE);
    }

    return capTableVersions;
  } catch (error) {
    return throwError(CAP_TABLE_VERSIONS_BY_COMPANY_AND_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetCapTableVersionsByCompanyAndMD: UseGetCapTableVersionsByCompanyAndMD = params => {
  const { company, measurementDate, shouldQueryAutomatically = true, excludePrivateVersions } = params;

  const companyId = company?.id;
  const measurementDateId = measurementDate?.md_id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetCapTableVersionsByCompanyAndMDHook['data'],
    Error
  >({
    enabled: !isNil(companyId) && !isNil(measurementDateId) && shouldQueryAutomatically,
    queryFn: () => getCapTableVersionsByCompanyAndMD({ companyId, measurementDateId, excludePrivateVersions }),
    queryKey: [CAP_TABLE_VERSIONS_BY_COMPANY_AND_MD_QUERY_KEY, companyId, measurementDateId, excludePrivateVersions ?? false],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetCapTableVersionsByCompanyAndMD;
